import { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';

declare global {
    interface Window {
        turnstile?: any;
        onTurnstileLoad?: () => void;
    }
}

interface TurnstileWidgetProps {
    siteKey?: string;
    onToken: (token: string) => void;
    onError?: () => void;
    onExpire?: () => void;
    onTimeout?: () => void;
    onReady?: () => void;
    className?: string;
    action?: string;
    appearance?: 'always' | 'execute' | 'interaction-only';
}

export interface TurnstileWidgetRef {
    reset: () => void;
    execute: () => boolean;
    getResponse: () => string | undefined;
    getStatus: () => { ready: boolean; executing: boolean; widgetId: string | null };
}

export const TurnstileWidget = forwardRef<TurnstileWidgetRef, TurnstileWidgetProps>(
    ({ siteKey, onToken, onError, onExpire, onTimeout, onReady, className, action = 'contest_entry', appearance = 'interaction-only' }, ref) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const widgetIdRef = useRef<string | null>(null);
        const [isScriptLoaded, setIsScriptLoaded] = useState(false);
        const [isReady, setIsReady] = useState(false);
        const [isExecuting, setIsExecuting] = useState(false);
        const [error, setError] = useState<string | null>(null);

        // Expose imperative methods via ref
        useImperativeHandle(ref, () => ({
            reset: () => {
                setIsExecuting(false);
                if (window.turnstile && widgetIdRef.current) {
                    window.turnstile.reset(widgetIdRef.current);
                }
            },
            execute: () => {
                if (!isReady || !window.turnstile || !widgetIdRef.current) {
                    return false;
                }
                if (isExecuting) {
                    return false;
                }

                setIsExecuting(true);
                window.turnstile.reset(widgetIdRef.current);
                window.turnstile.execute(widgetIdRef.current);

                return true;
            },
            getResponse: () => {
                if (window.turnstile && widgetIdRef.current) {
                    return window.turnstile.getResponse(widgetIdRef.current);
                }
                return undefined;
            },
            getStatus: () => ({
                ready: isReady,
                executing: isExecuting,
                widgetId: widgetIdRef.current
            })
        }));

        useEffect(() => {
            if (!siteKey) {
                setError("Missing Site Key");
                if (onError) onError();
                return;
            }

            // Load script if not present
            if (!document.getElementById('cf-turnstile-script')) {
                const script = document.createElement('script');
                script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
                script.id = 'cf-turnstile-script';
                script.async = true;
                script.defer = true;
                document.body.appendChild(script);

                script.onload = () => {
                    setIsScriptLoaded(true);
                };
                script.onerror = () => {
                    setError("Failed to load Turnstile script");
                    if (onError) onError();
                };
            } else {
                if (window.turnstile) {
                    setIsScriptLoaded(true);
                } else {
                    const interval = setInterval(() => {
                        if (window.turnstile) {
                            clearInterval(interval);
                            setIsScriptLoaded(true);
                        }
                    }, 100);
                    // Clear interval after 5s to prevent infinite checking
                    const timeout = setTimeout(() => clearInterval(interval), 5000);
                    return () => {
                        clearInterval(interval);
                        clearTimeout(timeout);
                    };
                }
            }

            return () => {
                if (window.turnstile && widgetIdRef.current) {
                    try {
                        window.turnstile.remove(widgetIdRef.current);
                        widgetIdRef.current = null;
                        setIsReady(false);
                    } catch (e) {
                        // Ignore removal errors
                    }
                }
            };
        }, [siteKey]);

        // Effect to render widget once script is loaded
        useEffect(() => {
            if (isScriptLoaded && containerRef.current && !widgetIdRef.current && siteKey) {
                try {
                    const id = window.turnstile.render(containerRef.current, {
                        sitekey: siteKey,
                        action,
                        appearance,
                        'refresh-expired': 'auto',
                        callback: (token: string) => {
                            setIsExecuting(false);
                            onToken(token);
                        },
                        'error-callback': () => {
                            setIsExecuting(false);
                            onError && onError();
                        },
                        'expired-callback': () => {
                            setIsExecuting(false);
                            onExpire && onExpire();
                        },
                        'timeout-callback': () => {
                            setIsExecuting(false);
                            onTimeout && onTimeout();
                        },
                    });
                    widgetIdRef.current = id;
                    setIsReady(true);
                    if (onReady) onReady();

                } catch (err) {
                    console.error("Turnstile render error:", err);
                    setError("Render Error");
                    if (onError) onError();
                }
            }
        }, [isScriptLoaded, siteKey, action, appearance, onToken, onError, onExpire, onTimeout, onReady]);

        if (error) {
            return null;
        }

        return (
            <div
                ref={containerRef}
                className={className}
                style={{ minHeight: appearance === 'always' ? '65px' : '0px' }}
            />
        );
    }
);

TurnstileWidget.displayName = 'TurnstileWidget';
